import { useEffect, useMemo, useRef, useState } from 'react'
import clsx from 'clsx'
import {
  addProtocol, Map as MlMap, NavigationControl, Popup, ScaleControl, setWorkerUrl,
  type ExpressionSpecification,
} from 'maplibre-gl'
import workerUrl from 'maplibre-gl/dist/maplibre-gl-worker.mjs?worker&url'
import 'maplibre-gl/dist/maplibre-gl.css'
import { Protocol } from 'pmtiles'
import { createGoogle2dSession, rasterBasemap, type BasemapMode } from '../map/basemaps'
import { buildStyle } from '../map/style'
import { useConfig } from '../lib/useConfig'
import { pruneTweens, seenSFixEpochMs, tweenedPosition, type TweenStore } from '../map/layers/fixTween'
import type { PhotorealHandle } from '../map/photoreal'
import { attachNetradyneCameraHandlers, netradyneCameraButtonHtml } from '../map/netradyneCameraPopup'
import { MapLegendCard } from '../cc/MapLegendCard'
import {
  BUS_ICON_PIXEL_RATIO, BUS_MODELS, busIconImageData, busModel,
} from './busIcons'
import {
  BUS_FOCUS_MIN_ZOOM, nextStopPosition, replayFitKey, resolveBusFocus, stopBeadFeatures,
} from './busFocus'
import {
  fetchMbmtRouteStops, nextRouteStop,
  type MbmtRoute, type MbmtRouteStop, type MbmtStage, type MbmtVehicle,
} from './useMbmtApi'

/** Mira-Bhayandar civic centre — MBMT's operating box, not the Ultratech corridor. */
const MBMT: [number, number] = [72.854, 19.287]

export interface MbmtReplayOverlay {
  line: [number, number][]
  playhead: [number, number] | null
  geotags: { lon: number; lat: number; name: string }[]
  /** Alert markers -- colored and labeled by alert kind, distinct from the plain blue
   * geotag dots above. Set by every replay modal that has alert data to show
   * (MbmtTripAlerts.tsx's trip replay, MbmtDutyPlayback.tsx's duty replay, and
   * MbmtEvidence.tsx's breakdown-evidence replay); omitted only by the plain live wall,
   * which never has a `replay` at all. Presence of this field is also what gates the
   * fit-bounds-to-trip effect below from re-fitting on every tick of a ticking replay --
   * see replayFitKey (busFocus.ts). */
  alerts?: { lon: number; lat: number; kind: string; message?: string | null }[]
}

/** Same five kinds GET /api/mbmt/trip-alerts counts per trip -- one fixed colour per kind
 * so a marker's colour always means the same thing on every replay, not just within one
 * trip's legend. */
const REPLAY_ALERT_COLOR: Record<string, string> = {
  HARSH_BRAKE: '#F43F5E',
  HARSH_ACCEL: '#F59E0B',
  OVERSPEED: '#DC2626',
  UNSCHEDULED_STOP: '#8B5CF6',
  EXCESSIVE_IDLING: '#0EA5E9',
}
const REPLAY_ALERT_COLOR_DEFAULT = '#64748B'

let workerSet = false
function ensureWorker(): void {
  if (workerSet) return
  setWorkerUrl(workerUrl)
  // pmtiles:// has to be registered before any Map is constructed -- this map ran on
  // plain HTTP raster tiles until now and never needed it. Same idiom as MapCanvas.tsx /
  // BusRouteMap.tsx, each with their own module-scoped guard.
  addProtocol('pmtiles', new Protocol().tile)
  workerSet = true
}

type BusStatus = 'down' | 'moving' | 'idle' | 'stale' | 'not_scheduled' | 'off_route'

export const BUS_STATUS_STYLE: Record<BusStatus, { fill: string; edge: string }> = {
  down: { fill: '#F43F5E', edge: '#9F1239' },
  moving: { fill: '#10B981', edge: '#047857' },
  idle: { fill: '#F59E0B', edge: '#B45309' },
  not_scheduled: { fill: '#0EA5E9', edge: '#0369A1' },
  off_route: { fill: '#EA580C', edge: '#9A3412' },
  stale: { fill: '#94A3B8', edge: '#64748B' },
}

export function busStatus(v: {
  down: boolean
  moving: boolean
  stale: boolean
  off_route?: boolean
  not_scheduled?: boolean
}): BusStatus {
  // Priority: breakdown > offline > off route > not scheduled > running > idle.
  if (v.down) return 'down'
  if (v.stale) return 'stale'
  if (v.off_route) return 'off_route'
  if (v.not_scheduled) return 'not_scheduled'
  if (v.moving) return 'moving'
  return 'idle'
}

/** Same staleness threshold the animation loop's `positioned` map below uses
 * (`stale: v.seen_s >= STALE_SECONDS`) -- one shared constant, not two numbers that could
 * drift apart. (The legend and the markers can still legitimately disagree on WHICH
 * vehicles they cover -- see legendRowsFor()'s own comment -- just never on what counts
 * as stale for a vehicle both of them do cover.) */
const STALE_SECONDS = 90

const LEGEND_ROW_ORDER: { status: BusStatus; label: string }[] = [
  { status: 'moving', label: 'Running' },
  { status: 'idle', label: 'Idle' },
  { status: 'down', label: 'Breakdown' },
  { status: 'not_scheduled', label: 'Not scheduled' },
  { status: 'off_route', label: 'Off route' },
  { status: 'stale', label: 'No data/offline' },
]

/**
 * Fleet-status legend rows for the live map, one row per `BusStatus` in a fixed order,
 * always all six (never collapsed to nothing on an empty fleet). Pure and exported --
 * same testing convention this file already uses for mbmtFixEpochMs/rotatedRect/
 * busModelParts, since the component itself needs a real WebGL canvas jsdom can't give it.
 * Classification reuses busStatus() itself (down > stale > off_route > not_scheduled >
 * moving > idle) rather than reimplementing the priority a second time.
 *
 * This is a FLEET summary, not a marker-pixel count: called with `fleetVehicles` (the
 * full roster) rather than `vehicles` (the marker loop's own `down`/`moving`/`seen_s`
 * classification), it counts every known vehicle by status even one with no lat/lon fix
 * at all -- which the marker loop filters out (`Number.isFinite(v.lat) && ...lon`) since
 * it has nothing to draw. A GPS-less vehicle is squarely a `No data/offline` case and an
 * operationally important one to surface, not something that should silently vanish from
 * both the map AND the one place that would otherwise report it. `down` still wins: a
 * breakdown with no fix is a Breakdown, not an offline bus.
 */
function hasMapFix(v: { lat?: number | null; lon?: number | null }): boolean {
  return Number.isFinite(v.lat) && Number.isFinite(v.lon)
}

export function legendRowsFor(
  vehicles: {
    vehicle_id: string
    moving?: boolean
    seen_s: number
    lat?: number | null
    lon?: number | null
    schedule_code?: string | null
    off_route?: boolean
  }[],
  down: Set<string>,
): { icon: 'dot'; color: string; label: string; count: number }[] {
  const counts: Record<BusStatus, number> = {
    down: 0, moving: 0, idle: 0, stale: 0, not_scheduled: 0, off_route: 0,
  }
  for (const v of vehicles) {
    // schedule_code undefined (unit fixtures / partial props) is not "Not scheduled" --
    // only an explicit empty string from /live means TIMS has no duty for the plate.
    const status = busStatus({
      down: down.has(v.vehicle_id),
      moving: Boolean(v.moving),
      stale: !hasMapFix(v) || v.seen_s >= STALE_SECONDS,
      off_route: Boolean(v.off_route),
      not_scheduled: v.schedule_code !== undefined && !(v.schedule_code || '').trim(),
    })
    counts[status] += 1
  }
  return LEGEND_ROW_ORDER.map(({ status, label }) => ({
    icon: 'dot' as const,
    color: BUS_STATUS_STYLE[status].fill,
    label,
    count: counts[status],
  }))
}

/**
 * MBMT/TUPL have no absolute fix timestamp on the wire, only `seen_s` (seconds-ago as of
 * the poll) plus the poll's own fetch wall-clock -- `seenSFixEpochMs` (fixTween.ts, shared
 * with TuplLiveMap.tsx's own `tuplFixEpochMs`) derives the real fix epoch those two
 * together imply, feeding the shared fixTween engine in place of the old
 * coastBusPosition/capCoastAgeToStop dead-reckoning pair. Re-exported under this file's
 * own name so MbmtLiveMap.test.ts's locked `import { mbmtFixEpochMs } from
 * './MbmtLiveMap'` contract is unaffected by the two files sharing one implementation.
 */
export const mbmtFixEpochMs = seenSFixEpochMs

// Deliberately NOT real bus dimensions (a real 8m x 2.6m bus rendered at true scale was
// confirmed live to read as "a container" -- practically invisible next to even a small
// building at MBMT's usual zoom, 12-16). Sized instead for legibility at that zoom without
// forcing a zoom-in, the same reasoning the flat icon's own icon-size scale already used.
const BUS_LENGTH_M = 26
const BUS_WIDTH_M = 11
const BUS_BODY_HEIGHT_M = 12

const METERS_PER_DEG_LAT = 111_320

/**
 * A rotated rectangle footprint centred `forwardM`/`rightM` from (lat, lon), in [lon, lat]
 * pairs (closed ring), oriented by compass heading (0=north/+lat, 90=east/+lon) -- the
 * same convention the tick loop's own `heading` property (fed straight from v.heading_deg)
 * uses for the flat icon's rotation, so the 3D block and the icon never disagree on what a
 * given heading_deg means on this map. The offset lets busModelParts() below place
 * a windshield, wheels and headlights relative to the bus's own body instead of only ever
 * drawing one rectangle centred on the GPS fix.
 *
 * Ring order matters here, not just cosmetically: GeoJSON exterior rings are wound
 * counter-clockwise (RFC 7946), and fill-extrusion derives which face is the roof from
 * that winding. An earlier version wound clockwise (confirmed by the shoelace sign) --
 * extrusions still drew, but with their top face's normal pointing the wrong way, which is
 * why buses "were not visible in any form" depending on approach angle: a backface-culled
 * roof reads as a hole straight through the block from some camera angles and fine from
 * others, not as a uniform bug every bus shared alike.
 */
export function rotatedRect(
  lat: number, lon: number, headingDeg: number,
  forwardM: number, rightM: number, lengthM: number, widthM: number,
): [number, number][] {
  const rad = (headingDeg * Math.PI) / 180
  const cosLat = Math.max(0.2, Math.cos((lat * Math.PI) / 180))
  const sinR = Math.sin(rad)
  const cosR = Math.cos(rad)
  // Local frame: +x forward (length), +y right (width), rotated into the compass bearing.
  const corner = (x: number, y: number): [number, number] => {
    const east = x * sinR + y * cosR
    const north = x * cosR - y * sinR
    return [lon + east / cosLat / METERS_PER_DEG_LAT, lat + north / METERS_PER_DEG_LAT]
  }
  const hl = lengthM / 2
  const hw = widthM / 2
  const frontLeft = corner(forwardM + hl, rightM - hw)
  return [
    frontLeft,
    corner(forwardM - hl, rightM - hw),
    corner(forwardM - hl, rightM + hw),
    corner(forwardM + hl, rightM + hw),
    frontLeft,
  ]
}

export type BusModelPartKind = 'body' | 'glass' | 'wheel' | 'light'

export interface BusModelPart {
  ring: [number, number][]
  base: number
  height: number
  kind: BusModelPartKind
}

/**
 * The 3D-view "3D bus" -- no .glb asset (style.ts has none, and sourcing/licensing one is
 * a separate, much bigger undertaking than this map needed), just enough native MapLibre
 * geometry stacked up to read as a bus rather than as a plain coloured block: a body, a
 * windshield standing slightly proud of the roofline, four wheels peeking out at the
 * sides, and a pair of headlights at the front. Confirmed live that the single-box version
 * read as "a container" -- these are what a bus actually has that a shipping container
 * doesn't.
 *
 * All proportions are fractions of BUS_LENGTH_M/WIDTH_M/BODY_HEIGHT_M, not fixed metres,
 * so resizing the body (already once deliberately oversized for legibility -- see above)
 * keeps every part in proportion instead of a windshield sized for a 26m bus suddenly
 * looking wrong on a 12m one.
 */
export function busModelParts(
  lat: number, lon: number, headingDeg: number,
): BusModelPart[] {
  const rect = (forwardM: number, rightM: number, lengthM: number, widthM: number) =>
    rotatedRect(lat, lon, headingDeg, forwardM, rightM, lengthM, widthM)

  const parts: BusModelPart[] = [
    { ring: rect(0, 0, BUS_LENGTH_M, BUS_WIDTH_M), base: 0, height: BUS_BODY_HEIGHT_M, kind: 'body' },
  ]

  // Windshield: front ~28% of the body, upper half, standing slightly proud of the roof
  // line so it reads as a cab window rather than as a second colour painted on the box.
  const glassLen = BUS_LENGTH_M * 0.28
  parts.push({
    ring: rect(BUS_LENGTH_M / 2 - glassLen / 2, 0, glassLen, BUS_WIDTH_M * 0.92),
    base: BUS_BODY_HEIGHT_M * 0.5, height: BUS_BODY_HEIGHT_M * 1.02, kind: 'glass',
  })

  // Wheels: four short dark blocks flush with each side, at front and rear axle position.
  const wheelLen = BUS_LENGTH_M * 0.16
  const wheelWidth = BUS_WIDTH_M * 0.14
  const axleOffset = BUS_LENGTH_M * 0.32
  for (const forwardM of [axleOffset, -axleOffset]) {
    for (const side of [1, -1]) {
      parts.push({
        ring: rect(forwardM, side * (BUS_WIDTH_M / 2), wheelLen, wheelWidth),
        base: 0, height: BUS_BODY_HEIGHT_M * 0.22, kind: 'wheel',
      })
    }
  }

  // Headlights: two small bright blocks at the front corners, bumper height.
  const lightLen = BUS_LENGTH_M * 0.05
  const lightWidth = BUS_WIDTH_M * 0.16
  for (const side of [1, -1]) {
    parts.push({
      ring: rect(
        BUS_LENGTH_M / 2 - lightLen / 2, side * (BUS_WIDTH_M / 2 - lightWidth / 2),
        lightLen, lightWidth,
      ),
      base: BUS_BODY_HEIGHT_M * 0.14, height: BUS_BODY_HEIGHT_M * 0.34, kind: 'light',
    })
  }

  return parts
}

/** Fixed colours for the parts that don't carry a live status -- only the body does. */
const BUS_PART_COLOR: Record<Exclude<BusModelPartKind, 'body'>, string> = {
  glass: '#0F3D5C',
  wheel: '#0F172A',
  light: '#FDE68A',
}

export function MbmtLiveMap({
  vehicles,
  fleetVehicles,
  routes,
  stages,
  down,
  selected,
  onSelect,
  replay,
  selectedRouteCode,
  stopDirection,
  onStopDirectionChange,
  focusTick,
  focusFallback,
  showFleetLegend = true,
}: {
  /** Markers actually drawn on the map. On the plain live wall this is always the full
   *  roster -- duty playback and breakdown-evidence playback (MbmtDutyPlayback.tsx,
   *  MbmtEvidence.tsx) each mount their OWN embedded MbmtLiveMap with `vehicles={[]}`
   *  instead of narrowing this shared instance, so this prop no longer carries a replay
   *  subset the legend below would need to ignore. */
  vehicles: MbmtVehicle[]
  /** The full live roster, for the fleet-status legend -- always the complete fleet.
   *  Required, not optional with a `vehicles` fallback: this component can never be
   *  mounted in a test (real WebGL canvas), so a silent fallback here would be
   *  permanently unguarded by anything -- a missing prop should fail to compile, not fail
   *  silently at runtime. */
  fleetVehicles: MbmtVehicle[]
  routes: MbmtRoute[]
  stages: MbmtStage[]
  down: Set<string>
  selected: string | null
  onSelect: (id: string) => void
  /** Bumped on every fleet/alert click so re-selecting the same plate still pans. */
  focusTick?: number
  /** Alert/breakdown pin used when the selected bus has no live GPS. */
  focusFallback?: { lat?: number | null; lon?: number | null } | null
  /** An explicit route pick (from a routes panel) takes priority over inferring a route
   * from whichever vehicle happens to be selected -- picking a route with no bus selected
   * must still highlight it. */
  selectedRouteCode?: string | null
  /** Controlled/uncontrolled split (Command Centre rework Phase 4): when provided, this
   *  overrides the internal up/down toggle state and the toggle buttons call
   *  `onStopDirectionChange` instead of the internal setter -- MbmtDutyPlayback.tsx is the
   *  only caller that passes both, so its own "stop arrival times" section (built from the
   *  same stops at the same direction) never silently disagrees with what the embedded
   *  map's beads are showing. Every other caller omits both and keeps today's
   *  internal-state behavior unchanged. */
  stopDirection?: 'up' | 'down'
  onStopDirectionChange?: (d: 'up' | 'down') => void
  replay?: MbmtReplayOverlay | null
  /** Live wall only. Trip replay mounts this same map with an empty roster; leaving the
   *  legend on paints Running/Idle/Breakdown/No data all at 0 over a historical trip. */
  showFleetLegend?: boolean
}): JSX.Element {
  const container = useRef<HTMLDivElement>(null)
  const mapRef = useRef<MlMap | null>(null)
  const ready = useRef(false)
  const [mapReady, setMapReady] = useState(false)
  const onSelectRef = useRef(onSelect)
  onSelectRef.current = onSelect
  const hadRoute = useRef(false)
  const highlightRoute = selectedRouteCode
    || vehicles.find((v) => v.vehicle_id === selected)?.route_code
    || null
  const legendRows = useMemo(
    () => legendRowsFor(fleetVehicles, down),
    [fleetVehicles, down],
  )

  // Free tilt-and-orbit on a selection, opt-in: off by default so routine 2D ops never
  // gets an unrequested camera animation, on when the operator asks for it.
  const [view3D, setView3D] = useState(false)
  // Up and Down are two different physical stop sequences (site_route_stop's own
  // convention -- a loop's outbound and return legs, not one continuous round-trip), so
  // showing both on the map at once is showing two routes overlaid, color-coded or not.
  // One direction at a time is what "segregated" actually means here.
  //
  // Controlled/uncontrolled split (Command Centre rework Phase 4): `stopDirection` (the
  // prop) wins when passed; `internalStopDirection` is the fallback every existing,
  // uncontrolled caller keeps using exactly as before. `setStopDirection` stays the one
  // function the toggle buttons call either way -- it is what decides, not the caller.
  const [internalStopDirection, setInternalStopDirection] = useState<'up' | 'down'>('up')
  const activeStopDirection = stopDirection ?? internalStopDirection
  const setStopDirection = (d: 'up' | 'down'): void => (onStopDirectionChange ? onStopDirectionChange(d) : setInternalStopDirection(d))

  // `configLoaded` gates map construction -- not just a style swap after the fact --
  // because MapLibre does not preserve custom sources/layers across setStyle(), and every
  // layer this map adds lives inside the single 'load' handler below, which only ever runs
  // once. `basemapMode` below is resolved BEFORE construction for the same reason: deciding
  // it once, up front, avoids needing MapCanvas.tsx's live setStyle()-and-re-add-every-layer
  // machinery, which this map's one-shot 'load' handler was never built for.
  const { loaded: configLoaded, mbmt_tiles: hasTilesCfg, mbmt_tiles_version: tilesVersionCfg,
    photoreal: photorealAvailableCfg, maps_key: mapsKeyCfg } = useConfig()
  const hasTiles = Boolean(hasTilesCfg)
  const tilesVersion = tilesVersionCfg || ''
  // Photoreal is ADDITIVE, not a replacement for the free buildings-3d path above -- the
  // user explicitly asked for it here too, but the free layer stays the default and this
  // stays opt-in, same reasoning BusRouteMap.tsx's own photoreal toggle uses. Mutually
  // exclusive with view3D at the UI level only (see the two onClick handlers below) so the
  // two 3D representations never render on top of each other -- nothing in the existing
  // buildings-3d/mbmt-bus-blocks effect above needed to change for that.
  const [photorealOn, setPhotorealOn] = useState(false)
  const photorealAvailable = Boolean(photorealAvailableCfg)
  const mapsKey = mapsKeyCfg || ''
  const [photorealAttribution, setPhotorealAttribution] = useState<string[]>([])
  const photorealRef = useRef<PhotorealHandle | null>(null)

  // Google tiles are now the DEFAULT base layer here, same reasoning MapCanvas.tsx (the
  // Ultratech corridor map) already proved out: the self-hosted pmtiles archive plus a
  // community openstreetmap.org raster overlay on top of it (this map's old setup) is TWO
  // remote-ish tile sources that both have to succeed, and confirmed live 2026-10-23: a
  // pmtiles fetch failure left the console's own `pmtiles` library throwing unhandled
  // "Failed to fetch" errors with no retry, and this map's bus/route/stage layers -- every
  // one of them added inside the SAME 'load' handler as the basemap -- never appeared at
  // all. Resolved ONCE, before construction (not a live switch after the fact -- see above):
  // `null` while deciding, then 'google2d' on a successful session or 'vector' (the old
  // self-hosted default) on any failure or on a 4s timeout, so a slow/unreachable Maps
  // Platform endpoint degrades to the offline archive instead of stalling the whole map.
  const [basemapMode, setBasemapMode] = useState<BasemapMode | null>(null)
  useEffect(() => {
    if (!configLoaded) return
    if (!mapsKey) {
      setBasemapMode('vector')
      return
    }
    let settled = false
    const settle = (mode: BasemapMode): void => {
      if (settled) return
      settled = true
      setBasemapMode(mode)
    }
    const timeout = window.setTimeout(() => settle('vector'), 4000)
    createGoogle2dSession(mapsKey)
      .then((ok) => settle(ok ? 'google2d' : 'vector'))
      .catch(() => settle('vector'))
      .finally(() => window.clearTimeout(timeout))
    return () => window.clearTimeout(timeout)
  }, [configLoaded, mapsKey])

  useEffect(() => {
    if (!container.current || !configLoaded || !basemapMode || mapRef.current) return
    ensureWorker()
    const map = new MlMap({
      container: container.current,
      // rasterOverlay (rasterBasemap('osm')) only matters in buildStyle's 'vector' branch
      // (painted under the old self-hosted-archive 2D look) -- inert, harmlessly ignored,
      // when basemapMode is 'google2d', which takes buildStyle's early raster-replace path.
      style: buildStyle(hasTiles, tilesVersion, 'light', basemapMode, 'mbmt', rasterBasemap('osm')),
      center: MBMT,
      zoom: 12.2,
      attributionControl: { compact: true },
    })
    mapRef.current = map
    // No stack trace, no failing test for a blank map -- a source/tile error previously
    // vanished into pmtiles' own unhandled rejection with nothing logged here at all (the
    // confirmed-live 2026-10-23 incident this basemapMode effect above exists to avoid
    // repeating). This does not attempt MapCanvas.tsx's live fallback-to-vector (that needs
    // its style.load-driven re-add-every-layer machinery, which this map's one-shot 'load'
    // handler was never built for -- see basemapMode's own comment) -- just makes a future
    // failure visible instead of silent.
    map.on('error', (e) => console.error('[mbmt-map]', e.error?.message ?? e))
    map.addControl(new NavigationControl({ showCompass: false }), 'top-right')
    map.addControl(new ScaleControl({ unit: 'metric' }), 'bottom-left')
    const popup = new Popup({ closeButton: false, closeOnClick: false, offset: 12 })
    const clickPopup = new Popup({ closeButton: true, closeOnClick: false, offset: 16 })

    map.on('load', () => {
      // The shared LIGHT palette (style.ts) is tuned for Ultratech's command-centre wall,
      // where the map is meant to recede behind saturated fleet markers -- roads/labels
      // barely above the earth colour is the point there. This map is the opposite: an
      // operator reads street names and road shapes to make sense of where a bus actually
      // is, so those need real contrast. Overridden here at runtime, layer by layer,
      // rather than touching the shared palette, so Ultratech's tuned wall look is
      // untouched. Layer ids are protomaps-themes-base's own (base_layers.ts), not
      // anything this file defines.
      for (const [id, color] of [
        ['roads_labels_major', '#334155'], ['roads_labels_minor', '#475569'],
        ['places_locality', '#1E293B'], ['places_subplace', '#334155'],
        ['address_label', '#64748B'],
      ] as const) {
        if (map.getLayer(id)) map.setPaintProperty(id, 'text-color', color)
      }
      for (const [id, color] of [
        ['roads_highway_casing_early', '#94A3B8'], ['roads_highway_casing_late', '#94A3B8'],
        ['roads_major_casing_early', '#9FAABB'], ['roads_major_casing_late', '#9FAABB'],
        ['roads_minor_casing', '#C3CCDB'], ['roads_minor_service_casing', '#CBD3E0'],
        ['roads_link_casing', '#9FAABB'],
      ] as const) {
        if (map.getLayer(id)) map.setPaintProperty(id, 'line-color', color)
      }

      map.addSource('mbmt-routes', {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      })
      const sel = ['==', ['get', 'selected'], true] as unknown as ExpressionSpecification
      const dim = ['==', ['get', 'dim'], true] as unknown as ExpressionSpecification
      // Google Maps' own route recipe: a wider casing line under a narrower colour line,
      // both round-capped, widths growing with zoom. The whole network draws in the Maps
      // blue family -- soft blue ribbons over a white edge, never grey -- and the selected
      // route is the saturated blue over a darker blue casing, sorted above its siblings
      // while the rest recede by opacity instead of colour.
      //
      // Created at the TOP of the stack -- the 2D-correct position, and the one this
      // needs on first load. It does NOT belong here permanently in 3D (buildings-3d must
      // occlude it there, see style.ts), but the view3D effect below is what moves it via
      // moveLayer() once 3D actually turns on; that effect only fires on a real view3D
      // change, and never runs during initial mount (ready.current is still false at that
      // point -- the map hasn't finished loading yet). Passing beforeId: 'buildings-3d'
      // right here instead was tried and is WRONG for the common case: a page that opens
      // straight into 2D and is never toggled to 3D would create the route already sitting
      // under buildings-3d -- fine for 3D, but also under osm-raster (the opaque 2D raster
      // basemap, painted well above buildings-3d's position), so the route never became
      // visible at all. Reported live: "on 2d if i click on vehicle, why route is not
      // highlighted?" -- it was highlighted, just painted underneath the raster tile.
      map.addLayer({
        id: 'mbmt-routes-casing',
        type: 'line',
        source: 'mbmt-routes',
        layout: {
          'line-cap': 'round',
          'line-join': 'round',
          'line-sort-key': ['case', sel, 2, 1],
        },
        paint: {
          'line-color': ['case', sel, '#1967D2', '#FFFFFF'],
          'line-width': [
            'interpolate', ['exponential', 1.6], ['zoom'],
            10, ['case', sel, 5, 3.2],
            14, ['case', sel, 11, 6.4],
            17, ['case', sel, 22, 12],
          ],
          'line-opacity': ['case', sel, 1, dim, 0.3, 0.9],
        },
      })
      map.addLayer({
        id: 'mbmt-routes',
        type: 'line',
        source: 'mbmt-routes',
        layout: {
          'line-cap': 'round',
          'line-join': 'round',
          'line-sort-key': ['case', sel, 2, 1],
        },
        paint: {
          'line-color': ['case', sel, '#4285F4', '#7BAAF7'],
          'line-width': [
            'interpolate', ['exponential', 1.6], ['zoom'],
            10, ['case', sel, 3.2, 1.8],
            14, ['case', sel, 7.5, 4],
            17, ['case', sel, 15, 8],
          ],
          'line-opacity': ['case', sel, 1, dim, 0.35, 0.82],
        },
      })
      // Surveyed boundaries (the depot; any future stage with a real polygon, not just a
      // stop's centroid) get their own fill+outline source -- separate from mbmt-stages'
      // point dots so a boundary doesn't have to fake itself as one giant circle.
      map.addSource('mbmt-stage-polygons', {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      })
      map.addLayer({
        id: 'mbmt-stage-polygons-fill',
        type: 'fill',
        source: 'mbmt-stage-polygons',
        paint: { 'fill-color': '#2563EB', 'fill-opacity': 0.12 },
      })
      map.addLayer({
        id: 'mbmt-stage-polygons-outline',
        type: 'line',
        source: 'mbmt-stage-polygons',
        paint: { 'line-color': '#2563EB', 'line-width': 2, 'line-opacity': 0.75 },
      })
      map.addSource('mbmt-stages', {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      })
      map.addLayer({
        id: 'mbmt-stages',
        type: 'circle',
        source: 'mbmt-stages',
        paint: {
          'circle-radius': 3.5,
          'circle-color': '#F8FAFC',
          'circle-stroke-width': 1.5,
          'circle-stroke-color': '#475569',
        },
      })
      // The highlighted route's own passenger stops (site_route_stop) for ONE direction at
      // a time (the stopDirection toggle below) -- Google-Maps-style white beads on the
      // blue ribbon, with names once you are close enough to care. Up and Down are two
      // different physical stop sequences, not one loop split in half, so showing both at
      // once was showing two routes overlaid; a colour-coded dot never actually segregated
      // them, it just tinted the same overlap.
      map.addSource('mbmt-route-stops', {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      })
      map.addLayer({
        id: 'mbmt-route-stops',
        type: 'circle',
        source: 'mbmt-route-stops',
        paint: {
          'circle-radius': ['interpolate', ['linear'], ['zoom'], 11, 2.5, 14, 4.5, 16, 6],
          'circle-color': '#FFFFFF',
          'circle-stroke-width': 2,
          'circle-stroke-color': '#1967D2',
        },
      })
      // The ONE bead ahead of the vehicle's own current/replayed position (Command Centre
      // rework Phase 4) -- a `next` boolean on each feature (set wherever the beads'
      // `setData` is pushed, below), filtered here rather than drawn from a second source,
      // so this stays perfectly in sync with the plain beads' own fetch/direction. Larger
      // and amber-filled, distinct from the plain white/blue beads underneath it. Added
      // BEFORE the labels layer (not after, an earlier adversarial-review finding) so the
      // one stop's name an operator most wants to read is not the one partly painted over
      // by its own highlight circle.
      map.addLayer({
        id: 'mbmt-route-stop-next',
        type: 'circle',
        source: 'mbmt-route-stops',
        filter: ['get', 'next'],
        paint: {
          'circle-radius': ['interpolate', ['linear'], ['zoom'], 11, 5, 14, 8, 16, 11],
          'circle-color': '#F59E0B',
          'circle-stroke-width': 2.5,
          'circle-stroke-color': '#FFFFFF',
          'circle-opacity': 0.9,
        },
      })
      map.addLayer({
        id: 'mbmt-route-stop-labels',
        type: 'symbol',
        source: 'mbmt-route-stops',
        minzoom: 13.5,
        layout: {
          'text-field': ['get', 'name'],
          'text-font': ['Noto Sans Regular'],
          'text-size': 10,
          'text-offset': [0, 0.9],
          'text-anchor': 'top',
          'text-optional': true,
        },
        paint: {
          'text-color': '#1E3A8A',
          'text-halo-color': '#FFFFFF',
          'text-halo-width': 1.3,
        },
      })
      for (const [status, sty] of Object.entries(BUS_STATUS_STYLE)) {
        for (const model of BUS_MODELS) {
          const id = `bus-${status}-${model}`
          if (!map.hasImage(id)) {
            map.addImage(id, busIconImageData(sty.fill, sty.edge, model), {
              pixelRatio: BUS_ICON_PIXEL_RATIO,
            })
          }
        }
      }
      map.addSource('mbmt-buses', {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      })
      // 3D view's actual "3D bus": no .glb asset, just native MapLibre geometry stacked
      // into a body + windshield + wheels + headlights (busModelParts()) -- same idea as
      // buildings-3d, standing in for a modelled asset nobody has to source or license.
      // height/base are per-FEATURE (each part has its own), not one constant for the
      // whole layer, since a wheel and the body it's attached to are not the same height.
      // Hidden by default; the view3D effect below swaps it in for the flat 'mbmt-buses'
      // icon layer, never both at once.
      map.addSource('mbmt-bus-blocks', {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      })
      map.addLayer({
        id: 'mbmt-bus-blocks',
        type: 'fill-extrusion',
        source: 'mbmt-bus-blocks',
        layout: { visibility: 'none' },
        paint: {
          'fill-extrusion-color': ['get', 'color'],
          'fill-extrusion-height': ['get', 'height'],
          'fill-extrusion-base': ['get', 'base'],
          'fill-extrusion-opacity': 0.96,
        },
      })
      map.addLayer({
        id: 'mbmt-pulse',
        type: 'circle',
        source: 'mbmt-buses',
        filter: ['==', ['get', 'down'], true],
        paint: {
          'circle-radius': 22,
          'circle-color': '#F43F5E',
          'circle-opacity': 0.18,
          'circle-blur': 0.4,
        },
      })
      // Selection reads as a ring under the glyph, not a recolour -- the icon keeps
      // telling you the bus's operational status even while it is selected.
      map.addLayer({
        id: 'mbmt-selected-ring',
        type: 'circle',
        source: 'mbmt-buses',
        filter: ['==', ['get', 'selected'], true],
        paint: {
          'circle-radius': 24,
          'circle-color': 'rgba(37,99,235,0.12)',
          'circle-stroke-width': 2,
          'circle-stroke-color': '#2563EB',
        },
      })
      map.addLayer({
        id: 'mbmt-buses',
        type: 'symbol',
        source: 'mbmt-buses',
        layout: {
          'icon-image': ['get', 'icon'],
          'icon-size': [
            'interpolate', ['linear'], ['zoom'],
            12, ['case', ['==', ['get', 'selected'], true], 0.42, 0.34],
            15, ['case', ['==', ['get', 'selected'], true], 0.58, 0.48],
            17, ['case', ['==', ['get', 'selected'], true], 0.78, 0.64],
          ],
          'icon-rotate': ['get', 'heading'],
          'icon-rotation-alignment': 'map',
          // Starts billboarded (viewport); the pitch/rotation effect below flips this to
          // 'map' the moment the camera actually tilts, so the bus visually leans with the
          // map in 3D instead of standing bolt upright while the ground rotates under it.
          // At pitch 0 (2D) the two alignments render identically, so this default is safe.
          'icon-pitch-alignment': 'viewport',
          'icon-allow-overlap': true,
          'icon-ignore-placement': true,
        },
      })
      map.addLayer({
        id: 'mbmt-bus-labels',
        type: 'symbol',
        source: 'mbmt-buses',
        minzoom: 14,
        layout: {
          'text-field': ['get', 'short'],
          'text-font': ['Noto Sans Medium'],
          'text-size': 10.5,
          'text-offset': [0, 2.4],
          'text-anchor': 'top',
          'text-optional': true,
        },
        paint: {
          'text-color': '#0F172A',
          'text-halo-color': '#FFFFFF',
          'text-halo-width': 1.4,
        },
      })
      map.addSource('mbmt-trail', {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      })
      map.addLayer({
        id: 'mbmt-trail',
        type: 'line',
        source: 'mbmt-trail',
        paint: { 'line-color': '#0F172A', 'line-width': 3, 'line-opacity': 0.55 },
      })
      map.addSource('mbmt-geotags', {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      })
      map.addLayer({
        id: 'mbmt-geotags',
        type: 'circle',
        source: 'mbmt-geotags',
        paint: {
          'circle-radius': 5,
          'circle-color': '#2563EB',
          'circle-stroke-width': 2,
          'circle-stroke-color': '#FFFFFF',
        },
      })
      map.addSource('mbmt-playhead', {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      })
      map.addLayer({
        id: 'mbmt-playhead',
        type: 'circle',
        source: 'mbmt-playhead',
        paint: {
          'circle-radius': 8,
          'circle-color': '#0F172A',
          'circle-stroke-width': 3,
          'circle-stroke-color': '#F8FAFC',
        },
      })
      // Replay-alert markers -- populated from replay.alerts, which the plain live-ops
      // wall never sets (it has no `replay` prop at all) but every replay modal does:
      // MbmtTripAlerts.tsx's trip replay, MbmtDutyPlayback.tsx's duty replay, and
      // MbmtEvidence.tsx's breakdown-evidence replay. This source/layer stays empty only
      // on the live wall.
      map.addSource('mbmt-replay-alerts', {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      })
      map.addLayer({
        id: 'mbmt-replay-alert-halo',
        type: 'circle',
        source: 'mbmt-replay-alerts',
        paint: {
          'circle-radius': ['interpolate', ['linear'], ['zoom'], 11, 10, 15, 16],
          'circle-color': ['get', 'color'],
          'circle-opacity': 0.18,
        },
      })
      map.addLayer({
        id: 'mbmt-replay-alert-markers',
        type: 'circle',
        source: 'mbmt-replay-alerts',
        paint: {
          'circle-radius': ['interpolate', ['linear'], ['zoom'], 11, 5, 15, 8],
          'circle-color': ['get', 'color'],
          'circle-stroke-width': 1.5,
          'circle-stroke-color': '#FFFFFF',
        },
      })
      map.addLayer({
        id: 'mbmt-replay-alert-labels',
        type: 'symbol',
        source: 'mbmt-replay-alerts',
        minzoom: 13,
        layout: {
          'text-field': ['get', 'label'],
          'text-font': ['Noto Sans Medium'],
          'text-size': 10,
          'text-offset': [0, 1.1],
          'text-anchor': 'top',
          'text-optional': true,
        },
        paint: {
          'text-color': '#0F172A',
          'text-halo-color': '#FFFFFF',
          'text-halo-width': 1.3,
        },
      })
      map.on('mouseenter', 'mbmt-replay-alert-markers', (e) => {
        map.getCanvas().style.cursor = 'pointer'
        const f = e.features?.[0]
        if (!f || f.geometry.type !== 'Point') return
        const p = f.properties ?? {}
        popup.setLngLat(f.geometry.coordinates as [number, number]).setHTML(
          `<div style="font:600 12px Inter,sans-serif;color:#0F172A">${p.label ?? ''}</div>`
          + (p.message
            ? `<div style="font:11px Inter,sans-serif;color:#64748B">${p.message}</div>`
            : ''),
        ).addTo(map)
      })
      map.on('mouseleave', 'mbmt-replay-alert-markers', () => {
        map.getCanvas().style.cursor = ''
        popup.remove()
      })
      // Bound to BOTH the flat icon (2D) and the extruded block (3D) -- only one of the
      // two is ever visible at once (the view3D effect below), but a hidden layer is also
      // excluded from hit-testing, so binding to just one left the other view unclickable.
      // Anchored on the feature's own lon/lat properties rather than e.geometry.coordinates
      // because that differs by layer (a Point for the icon, a Polygon ring for the block).
      const BUS_LAYERS = ['mbmt-buses', 'mbmt-bus-blocks'] as const
      for (const layerId of BUS_LAYERS) {
        map.on('click', layerId, (e) => {
          const f = e.features?.[0]
          const id = f?.properties?.id
          if (typeof id !== 'string' || !f) return
          onSelectRef.current(id)
          const p = f.properties ?? {}
          const anchor: [number, number] = [Number(p.lon), Number(p.lat)]
          // Same crew presentation as the detail panel's "Crew & duty" card -- one design
          // for the same data, floated at the bus so the operator never loses the map.
          clickPopup.setLngLat(anchor).setHTML(
            `<div style="min-width:180px;font-family:Inter,sans-serif">`
            + `<div style="font:700 12px ui-monospace,monospace;color:#0F172A">${id}</div>`
            + `<div style="font:600 13px Inter,sans-serif;color:#0F172A;margin-top:4px">`
            + `${p.driver || 'No TIMS duty on this plate today'}</div>`
            + `<div style="font:11px Inter,sans-serif;color:#64748B;margin-top:2px">`
            + `${p.sched ? `duty ${p.sched}` : '—'}`
            + `${p.shift ? ` · ${p.shift}` : ''}`
            + `${p.vtype ? ` · ${p.vtype}` : ''}`
            + `</div>`
            + `<div style="font:11px Inter,sans-serif;color:#64748B;margin-top:2px">`
            + `${p.route ? `route ${p.route} · ` : ''}`
            + `${p.speed ?? '—'} kph`
            + (p.soc != null && p.soc !== '' ? ` · ${Math.round(Number(p.soc))}% SoC` : '')
            + `</div>`
            + netradyneCameraButtonHtml()
            + `</div>`,
          ).addTo(map)
          // setHTML above replaces the popup's DOM every time, so the handler has to be
          // (re-)attached after each open, not once -- same requirement EimPortsMap.tsx's
          // MARK_BREAKDOWN_BTN_CLASS wiring already documents.
          const clickEl = clickPopup.getElement()
          if (clickEl) attachNetradyneCameraHandlers(clickEl, 'mbmt', id)
        })
        map.on('mouseenter', layerId, (e) => {
          map.getCanvas().style.cursor = 'pointer'
          const f = e.features?.[0]
          if (!f) return
          const p = f.properties ?? {}
          const anchor: [number, number] = [Number(p.lon), Number(p.lat)]
          const id = String(p.id ?? '')
          popup.setLngLat(anchor).setHTML(
            `<div style="font:600 12px ui-monospace,monospace;color:#0F172A">${id}</div>`
            + (p.driver
              ? `<div style="font:11px Inter,sans-serif;color:#0F172A">${p.driver}</div>`
              : '')
            + `<div style="font:11px Inter,sans-serif;color:#64748B">`
            + (p.route ? `route ${p.route} · ` : '')
            + `${p.speed ?? '—'} kph`
            + (p.soc != null && p.soc !== '' ? ` · ${Math.round(Number(p.soc))}% SoC` : '')
            + `</div>`
            + `<div style="font:11px Inter,sans-serif;color:#64748B">click to open the desk</div>`,
          ).addTo(map)
        })
        map.on('mouseleave', layerId, () => {
          map.getCanvas().style.cursor = ''
          popup.remove()
        })
      }
      map.on('click', (e) => {
        if (!map.queryRenderedFeatures(e.point, { layers: [...BUS_LAYERS] }).length) {
          clickPopup.remove()
        }
      })
      ready.current = true
      setMapReady(true)
    })
    const ro = new ResizeObserver(() => map.resize())
    ro.observe(container.current)
    return () => {
      popup.remove()
      clickPopup.remove()
      ro.disconnect()
      photorealRef.current?.destroy()
      photorealRef.current = null
      map.remove()
      mapRef.current = null
      ready.current = false
      setMapReady(false)
    }
    // hasTiles/tilesVersion deliberately excluded: they only matter at the instant
    // configLoaded flips true (same fetch, same tick), and the mapRef.current guard above
    // already makes this idempotent if it ever re-ran.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configLoaded, basemapMode])

  // Latest data for the animation loop below, via refs so the rAF effect (mount-only)
  // never needs to restart just because a new poll or selection came in.
  const busAnimVehiclesRef = useRef(vehicles)
  const busAnimDownRef = useRef(down)
  const busAnimSelectedRef = useRef(selected)
  // Wall-clock moment the current `vehicles` snapshot's seen_s values were true --
  // the anchor mbmtFixEpochMs() derives each vehicle's real fix epoch from.
  const busAnimFetchedAtRef = useRef(Date.now())
  // Real-fix-to-real-fix lerp state (fixTween.ts), mount-scoped like the RAF loop that
  // reads it -- replaces the old coastBusPosition/capCoastAgeToStop dead-reckoning pair.
  const tweenStoreRef = useRef<TweenStore>(new Map())
  useEffect(() => {
    busAnimVehiclesRef.current = vehicles
    busAnimFetchedAtRef.current = Date.now()
    pruneTweens(tweenStoreRef.current, new Set(
      vehicles.filter((v) => Number.isFinite(v.lat) && Number.isFinite(v.lon)).map((v) => v.vehicle_id),
    ))
  }, [vehicles])
  useEffect(() => { busAnimDownRef.current = down }, [down])
  useEffect(() => { busAnimSelectedRef.current = selected }, [selected])
  const busAnimView3DRef = useRef(view3D)
  useEffect(() => { busAnimView3DRef.current = view3D }, [view3D])
  // Throttle gate for the 3D bus-block source only -- see the tick loop below for why.
  const lastBlockUpdateRef = useRef(0)

  useEffect(() => {
    let raf = 0
    const tick = (): void => {
      raf = requestAnimationFrame(tick)
      const map = mapRef.current
      if (!map || !ready.current) return
      // Redraw every animation frame, not on a throttled beat -- a 250ms step is only
      // 4 positions/sec, which for a bus moving at city speed reads as discrete hops,
      // not a glide. ~57 points with collision placement already disabled (icon-allow-
      // overlap/icon-ignore-placement below) is cheap enough to retile on every frame.
      const src = map.getSource('mbmt-buses') as { setData: (d: object) => void } | undefined
      const blockSrc = map.getSource('mbmt-bus-blocks') as { setData: (d: object) => void } | undefined
      if (!src) return
      const now = Date.now()
      const fetchedAt = busAnimFetchedAtRef.current
      const downSet = busAnimDownRef.current
      const sel = busAnimSelectedRef.current
      // Computed once per bus, shared by both the flat icon (mbmt-buses) and the 3D block
      // (mbmt-bus-blocks) below -- same position, same status, so the two views of the
      // same bus can never show it in two different places or two different colours.
      const positioned = busAnimVehiclesRef.current
        .filter((v) => Number.isFinite(v.lat) && Number.isFinite(v.lon))
        .map((v) => {
          const flags = {
            down: downSet.has(v.vehicle_id),
            moving: Boolean(v.moving),
            stale: v.seen_s >= STALE_SECONDS,
            off_route: Boolean(v.off_route),
            not_scheduled: v.schedule_code !== undefined && !(v.schedule_code || '').trim(),
          }
          const status = busStatus(flags)
          const fixEpochMs = mbmtFixEpochMs(fetchedAt, v.seen_s)
          const pos = fixEpochMs == null
            ? { lat: v.lat as number, lon: v.lon as number }
            : tweenedPosition(
                tweenStoreRef.current, v.vehicle_id,
                { lat: v.lat as number, lon: v.lon as number },
                fixEpochMs, now,
              )
          return { v, flags, status, pos, heading: v.heading_deg ?? 0 }
        })
      // Shared by both features below (Point for the icon, Polygon for the block) -- the
      // click/hover handlers read lon/lat off properties rather than geometry precisely so
      // one property set works for either geometry type without a per-layer branch there.
      const withProps = positioned.map(({ v, flags, status, pos, heading }) => ({
        pos, heading, status,
        properties: {
          id: v.vehicle_id,
          ...flags,
          selected: v.vehicle_id === sel,
          icon: `bus-${status}-${busModel(v.vehicletype)}`,
          short: v.vehicle_id.slice(-4),
          driver: v.driver_name || '',
          sched: v.schedule_code || '',
          shift: v.schedule_shift || '',
          vtype: v.vehicletype || '',
          route: v.route_code || '',
          speed: v.speed_kph != null ? Math.round(v.speed_kph) : '—',
          soc: v.soc_pct ?? '',
          heading,
          lon: pos.lon,
          lat: pos.lat,
        },
      }))
      src.setData({
        type: 'FeatureCollection',
        features: withProps.map(({ properties, pos }) => ({
          type: 'Feature',
          properties,
          geometry: { type: 'Point', coordinates: [pos.lon, pos.lat] },
        })),
      })
      // Gated on view3D AND throttled, unlike the point source above. A GeoJSON source
      // backing fill-extrusion re-tessellates (earcut + wall geometry) on every setData,
      // not the cheap re-tile a Point source gets -- doing that 60x/sec for ~57 single-box
      // buses was confirmed live to freeze the tab solid, in 2D too, since the
      // tessellation cost is tied to the SOURCE being updated, not to whether the layer
      // reading it is currently visible. Skipped entirely outside 3D (the block isn't
      // shown, so there is nothing to buy by paying for it). 400ms, not the 250ms first
      // used for the single-box version: busModelParts() emits ~8 polygons per bus now
      // (body/glass/4 wheels/2 headlights), so the same per-update cost buys roughly 8x
      // less headroom at the same rate -- still comfortably smooth for a chunky 3D shape,
      // nowhere near the rate that froze the tab.
      if (blockSrc && busAnimView3DRef.current && now - lastBlockUpdateRef.current >= 400) {
        lastBlockUpdateRef.current = now
        blockSrc.setData({
          type: 'FeatureCollection',
          features: withProps.flatMap(({ properties, pos, heading, status }) =>
            busModelParts(pos.lat, pos.lon, heading).map((part) => ({
              type: 'Feature' as const,
              properties: {
                ...properties,
                color: part.kind === 'body' ? BUS_STATUS_STYLE[status].fill : BUS_PART_COLOR[part.kind],
                base: part.base,
                height: part.height,
              },
              geometry: { type: 'Polygon' as const, coordinates: [part.ring] },
            }))),
        })
      }
    }
    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }, [])


  useEffect(() => {
    const map = mapRef.current
    if (!map || !ready.current) return
    const routeSrc = map.getSource('mbmt-routes') as { setData: (d: object) => void } | undefined
    const stageSrc = map.getSource('mbmt-stages') as { setData: (d: object) => void } | undefined
    const stagePolySrc = map.getSource('mbmt-stage-polygons') as { setData: (d: object) => void } | undefined
    if (!routeSrc || !stageSrc || !stagePolySrc) return
    const selectedRoute = highlightRoute
    // Nothing drawn at all until a route or vehicle is selected -- the full network in
    // blue read as clutter on a live ops screen where only the picked route matters.
    routeSrc.setData({
      type: 'FeatureCollection',
      features: selectedRoute
        ? routes
          .filter((r) => r.route_code === selectedRoute)
          .map((r) => ({
            type: 'Feature',
            properties: { code: r.route_code, name: r.name, selected: true, dim: false },
            geometry: { type: 'LineString', coordinates: r.line },
          }))
        : [],
    })
    stageSrc.setData({
      type: 'FeatureCollection',
      features: stages
        .filter((s) => Number.isFinite(s.lat) && Number.isFinite(s.lon) && !s.polygon)
        .map((s) => ({
          type: 'Feature',
          properties: { name: s.name },
          geometry: { type: 'Point', coordinates: [s.lon, s.lat] },
        })),
    })
    stagePolySrc.setData({
      type: 'FeatureCollection',
      features: stages
        .filter((s) => s.polygon && s.polygon.length >= 3)
        .map((s) => ({
          type: 'Feature',
          properties: { name: s.name },
          geometry: { type: 'Polygon', coordinates: [s.polygon] },
        })),
    })
  }, [routes, stages, selected, vehicles, selectedRouteCode])

  // Kept in React state (not just pushed straight to the map source, as before Phase 4)
  // so the next-stop highlight below can be recomputed whenever either the stop list OR
  // the vehicle's position changes -- the two are independent update cadences (a stops
  // fetch happens once per route/direction; a live position or replay scrub changes far
  // more often) and the highlight effect needs to react to both.
  const [routeStops, setRouteStops] = useState<MbmtRouteStop[]>([])

  useEffect(() => {
    if (!highlightRoute) { setRouteStops([]); return }
    // Cleared before the fetch, not left showing the PREVIOUS direction's stops for one
    // network round-trip -- a toggle from Up to Down must not paint Up's beads under a
    // map that already reads "Down" (an adversarial-review finding).
    setRouteStops([])
    let live = true
    fetchMbmtRouteStops(highlightRoute, activeStopDirection)
      .then((stops) => { if (live) setRouteStops(stops) })
      .catch((e) => { if (live) { console.error('route-stops fetch failed', e); setRouteStops([]) } })
    return () => { live = false }
  }, [highlightRoute, activeStopDirection])

  // The next-stop highlight's position input, via the extracted `nextStopPosition` (so the
  // "replay wins when both could apply" precedence is independently unit-tested rather than
  // only provable by reading this call site): a replay's own scrub position when one exists
  // (the duty and trip-detail modals mount this map with `selected={null} vehicles={[]}`,
  // so it is the ONLY position available there and it moves as the operator scrubs),
  // otherwise the selected live vehicle's own GPS fix. `nextRouteStop` itself rejects
  // non-finite coordinates, so an absent vehicle or a null playhead simply yields no
  // highlight rather than needing a guard here too.
  const selectedVehicle = vehicles.find((v) => v.vehicle_id === selected)
  const { lat: nextStopLat, lon: nextStopLon } = nextStopPosition(
    selectedVehicle, replay?.playhead,
  )
  // moving is only meaningful for the live-vehicle position above, never for a replay
  // scrub (no "is it moving" concept on a historical playhead) -- keeps the bead on the
  // stop a dwelling bus actually arrived at instead of advancing to the next one and
  // reading as "Arriving" there for the whole idle window (live bug, confirmed against
  // production MBMT data 2026-10-11; see nextRouteStop's own doc).
  const nextStop = useMemo(
    () => nextRouteStop(
      routeStops, { lat: nextStopLat, lon: nextStopLon },
      replay?.playhead ? undefined : { moving: selectedVehicle?.moving },
    ),
    [routeStops, nextStopLat, nextStopLon, replay?.playhead, selectedVehicle?.moving],
  )

  useEffect(() => {
    const map = mapRef.current
    if (!map || !ready.current) return
    const src = map.getSource('mbmt-route-stops') as { setData: (d: object) => void } | undefined
    if (!src) return
    if (!highlightRoute) {
      src.setData({ type: 'FeatureCollection', features: [] })
      return
    }
    src.setData({ type: 'FeatureCollection', features: stopBeadFeatures(routeStops, nextStop) })
    // `mapReady` (state, triggers a re-render) alongside `ready.current` (the same ref read
    // above for a synchronous check) -- for the exact reason documented on the replay
    // data-push effect further down this file: `routeStops`/`nextStop` are both already
    // settled the instant a duty/trip-detail modal mounts (the route-stops fetch resolves
    // in parallel with the map's own async init, and the vehicle position is frozen until
    // Play), so without `mapReady` here this effect's one and only run can land before the
    // map exists and never get a second chance -- the beads and the next-stop highlight
    // then never reach the map at all until something else changes `routeStops`/`nextStop`
    // (an adversarial-review finding; same bug class the replay effects were already fixed
    // for in Phase 3).
  }, [highlightRoute, routeStops, nextStop, mapReady])

  // A new route selection starts back on Up rather than keeping whatever direction the
  // previous route was showing -- carrying "Down" over to a route the operator has not
  // looked at yet is a silent, easy-to-miss state, not a helpful default.
  useEffect(() => {
    setStopDirection('up')
  }, [highlightRoute])

  // The "overlayed 3d" read: tilt the camera while a route is highlighted AND 3D view is
  // switched on, flatten it otherwise. Only ease on the transition so the operator's own
  // pitch gestures are never fought mid-selection. Rotation is enabled for the same
  // window so a selection can be orbited, not just tilted. 3D is opt-in (view3D,
  // defaults off) rather than forced on every selection -- an operator clicking through
  // the fleet in routine 2D ops should not have the camera animate into a tilt they
  // never asked for.
  useEffect(() => {
    const map = mapRef.current
    if (!map || !ready.current) return
    const has = Boolean(highlightRoute) && view3D
    // Flip the bus layer to lie flat on the ground (and lean with the tilt) exactly when
    // the camera itself tilts -- billboarding it through a pitch change is what left buses
    // standing bolt upright while the map rotated under them.
    if (map.getLayer('mbmt-buses')) {
      map.setLayoutProperty('mbmt-buses', 'icon-pitch-alignment', has ? 'map' : 'viewport')
    }
    if (has === hadRoute.current) return
    hadRoute.current = has
    if (has) {
      map.easeTo({ pitch: 58, duration: 750 })
      map.dragRotate.enable()
      map.touchZoomRotate.enableRotation()
    } else {
      map.easeTo({ pitch: 0, bearing: 0, duration: 750 })
      map.dragRotate.disable()
      map.touchZoomRotate.disableRotation()
    }
  }, [highlightRoute, view3D])

  // Real 3D buildings (style.ts's 'buildings-3d' fill-extrusion layer, reading the
  // archive's own height/min_height fields), swapped in for the flat fill whenever 3D
  // view is on -- independent of highlightRoute, unlike the pitch above: buildings should
  // read as 3D the moment the toggle is on, not only once a route happens to be selected
  // too. This is what replaced the paid Google Photoreal toggle on this map.
  useEffect(() => {
    const map = mapRef.current
    if (!map || !ready.current) return
    if (!map.getLayer('buildings-3d')) return
    map.setLayoutProperty('buildings-3d', 'visibility', view3D ? 'visible' : 'none')
    if (map.getLayer('buildings')) {
      map.setLayoutProperty('buildings', 'visibility', view3D ? 'none' : 'visible')
    }
    // The raster overlay (style.ts's 'osm-raster' -- the openstreetmap.org look this map
    // always had) sits on top of everything in 2D, and gets out of the way for 3D since it
    // would otherwise paint over the vector buildings-3d layer underneath it.
    if (map.getLayer('osm-raster')) {
      map.setLayoutProperty('osm-raster', 'visibility', view3D ? 'none' : 'visible')
    }
    // The route line has no single correct position across both modes, so it MOVES
    // rather than sitting still -- same idea as the visibility swaps around it, just via
    // moveLayer() since a line layer has no on/off equivalent of "flat vs 3D" to toggle.
    // Reported live: after the route was moved to sit under buildings-3d (for 3D
    // occlusion -- see style.ts), clicking a vehicle in 2D no longer visibly highlighted
    // its route at all. Cause: osm-raster is an opaque raster image painted well above
    // buildings-3d's position in the style (index ~72 of ~89), so in 2D it now painted
    // straight over the route sitting at ~58 -- a regression the earlier 3D-only fix
    // never exercised, since osm-raster is hidden in 3D. In 3D the route belongs right
    // before buildings-3d (depth-tested occlusion); in 2D it belongs above osm-raster.
    // NOT above everything, though -- moveLayer() with no second argument moves a layer to
    // the very top of the ENTIRE style, which also buried mbmt-route-stops/mbmt-alerts/the
    // bus icons (all added after the route layers at creation time, specifically so they'd
    // paint on top of the route ribbon). Reported live: "stops hidden below routes in 2D."
    // 'mbmt-stage-polygons-fill' is the layer created immediately after the route layers
    // (see the addLayer calls above) -- anchoring here restores the exact creation-time
    // position: above osm-raster (routes were appended at the then-current top, which
    // already included the base style), below every app layer added afterward.
    if (map.getLayer('mbmt-routes-casing') && map.getLayer('mbmt-routes')) {
      if (view3D) {
        map.moveLayer('mbmt-routes-casing', 'buildings-3d')
        map.moveLayer('mbmt-routes', 'buildings-3d')
      } else if (map.getLayer('mbmt-stage-polygons-fill')) {
        map.moveLayer('mbmt-routes-casing', 'mbmt-stage-polygons-fill')
        map.moveLayer('mbmt-routes', 'mbmt-stage-polygons-fill')
      }
    }
    // Same swap for the bus markers: the flat icon in 2D, the modelled bus (real "3D
    // bus", see busModelParts()) in 3D -- never both, or the model reads as a shadow
    // under a sticker instead of as the bus itself.
    if (map.getLayer('mbmt-bus-blocks')) {
      map.setLayoutProperty('mbmt-bus-blocks', 'visibility', view3D ? 'visible' : 'none')
    }
    if (map.getLayer('mbmt-buses')) {
      map.setLayoutProperty('mbmt-buses', 'visibility', view3D ? 'none' : 'visible')
    }
  }, [view3D])

  // Photoreal 3D, additive -- see the state declarations above for why this doesn't touch
  // any of the free buildings-3d logic. Dynamically imported, same as every other photoreal
  // call site in this codebase: the overwhelming majority of sessions never turn it on.
  useEffect(() => {
    const map = mapRef.current
    if (!map || !mapReady) return
    if (photorealOn && !photorealRef.current && mapsKey) {
      let cancelled = false
      void import('../map/photoreal').then(({ addPhotoreal }) => {
        if (cancelled || photorealRef.current || !mapRef.current) return
        photorealRef.current = addPhotoreal(mapRef.current, mapsKey, {
          onLoad: () => {},
          onAttribution: (credits) => setPhotorealAttribution(credits),
          onError: () => { /* transient tile failure -- the toggle stays on, matching
                              BusRouteMap.tsx's own reasoning, not worth a page error */ },
        })
      })
      return () => { cancelled = true }
    }
    if (!photorealOn && photorealRef.current) {
      photorealRef.current.destroy()
      photorealRef.current = null
      setPhotorealAttribution([])
    }
  }, [photorealOn, mapsKey, mapReady])

  // Picking a route in a panel with no bus selected must still bring it into view --
  // vehicle selection already gets this via the easeTo effect below, a route pick had no
  // equivalent at all.
  useEffect(() => {
    const map = mapRef.current
    if (!map || !ready.current || !selectedRouteCode) return
    const route = routes.find((r) => r.route_code === selectedRouteCode)
    if (!route || route.line.length < 2) return
    let minLon = Infinity, minLat = Infinity, maxLon = -Infinity, maxLat = -Infinity
    for (const [lon, lat] of route.line) {
      if (lon < minLon) minLon = lon
      if (lon > maxLon) maxLon = lon
      if (lat < minLat) minLat = lat
      if (lat > maxLat) maxLat = lat
    }
    map.fitBounds([[minLon, minLat], [maxLon, maxLat]], { padding: 64, duration: 500 })
  }, [selectedRouteCode, routes])

  useEffect(() => {
    const map = mapRef.current
    if (!map || !ready.current) return
    const empty = { type: 'FeatureCollection', features: [] as object[] }
    const trail = map.getSource('mbmt-trail') as { setData: (d: object) => void } | undefined
    const tags = map.getSource('mbmt-geotags') as { setData: (d: object) => void } | undefined
    const head = map.getSource('mbmt-playhead') as { setData: (d: object) => void } | undefined
    const replayAlerts = map.getSource('mbmt-replay-alerts') as { setData: (d: object) => void } | undefined
    if (!trail || !tags || !head || !replayAlerts) return
    if (!replay || replay.line.length < 2) {
      trail.setData(empty)
    } else {
      trail.setData({
        type: 'Feature',
        geometry: { type: 'LineString', coordinates: replay.line },
        properties: {},
      })
    }
    tags.setData({
      type: 'FeatureCollection',
      features: (replay?.geotags ?? []).map((g) => ({
        type: 'Feature',
        properties: { name: g.name },
        geometry: { type: 'Point', coordinates: [g.lon, g.lat] },
      })),
    })
    head.setData({
      type: 'FeatureCollection',
      features: replay?.playhead
        ? [{
            type: 'Feature',
            properties: {},
            geometry: { type: 'Point', coordinates: replay.playhead },
          }]
        : [],
    })
    replayAlerts.setData({
      type: 'FeatureCollection',
      features: (replay?.alerts ?? [])
        .filter((a) => Number.isFinite(a.lat) && Number.isFinite(a.lon))
        .map((a) => ({
          type: 'Feature',
          properties: {
            kind: a.kind,
            label: (a.kind || 'alert').replace(/_/g, ' ').toLowerCase(),
            message: a.message || '',
            color: REPLAY_ALERT_COLOR[a.kind] ?? REPLAY_ALERT_COLOR_DEFAULT,
          },
          geometry: { type: 'Point', coordinates: [a.lon, a.lat] },
        })),
    })
    // `mapReady` (state, triggers a re-render) is required here alongside `ready.current`
    // (the same ref read elsewhere for a synchronous check) -- a duty or trip-detail replay
    // overlay is already fully built the instant the modal mounts and does not change again
    // until the operator presses Play (MbmtDutyPlayback's `overlay` is memoized on
    // `[duty, dutyPos]`, both stable until `at` first ticks). Without `mapReady` in this
    // array, this effect's one and only run can land BEFORE the map finishes its async
    // init (config fetch + maplibre's own `load` event), bail out on `!ready.current`, and
    // never fire again -- the trail/geotags/playhead/alert pins then never reach the map at
    // all until something else happens to change `replay`'s identity (pressing Play).
  }, [replay, mapReady])

  // A trip-detail or duty replay (replay.alerts set -- breakdown-clip replay never sets
  // it, so this never fires there) has no `selected` vehicle to pan the map's usual focus
  // effect off of, so it fits the camera to the trip's own line instead, same recipe as
  // the route-pick fitBounds above.
  //
  // Fits once per distinct trail, not once per render: a static trip-detail replay's
  // `replay` object is only rebuilt when a new clip loads, so "once per `replay` identity"
  // (the original recipe) was enough there. Duty playback rebuilds `overlay` every 200ms
  // tick (its `dutyPos`/`playhead` moves) even though the underlying trail is unchanged,
  // so gating on identity alone re-fit the camera ~5x/sec, fighting any pan/zoom the
  // operator made -- fixed once by gating on `playhead !== null` instead, which overshot
  // the other way and left the duty map never fitting its trail AT ALL (its playhead is
  // non-null from the very first tick, not just while ticking). Comparing the trail's own
  // endpoints handles both correctly: identical content across duty's ticks is a no-op,
  // while a genuinely different trail (a new clip, or a remounted duty modal for a
  // different vehicle) always has different endpoints and fits exactly once.
  const fittedLineKeyRef = useRef<string | null>(null)
  useEffect(() => {
    const map = mapRef.current
    const lineKey = replayFitKey(replay)
    if (!map || !ready.current || !lineKey || !replay) {
      fittedLineKeyRef.current = null
      return
    }
    if (fittedLineKeyRef.current === lineKey) return
    fittedLineKeyRef.current = lineKey
    let minLon = Infinity, minLat = Infinity, maxLon = -Infinity, maxLat = -Infinity
    for (const [lon, lat] of replay.line) {
      if (lon < minLon) minLon = lon
      if (lon > maxLon) maxLon = lon
      if (lat < minLat) minLat = lat
      if (lat > maxLat) maxLat = lat
    }
    if (minLon === Infinity) return
    map.fitBounds([[minLon, minLat], [maxLon, maxLat]], { padding: 72, duration: 500 })
    // `mapReady` is required for the same reason as the data-push effect above: a duty or
    // trip-detail overlay is stable from mount, so without it this effect's one run can
    // land before the map exists and never get a second chance to fit.
  }, [replay, mapReady])

  const vehiclesRef = useRef(vehicles)
  vehiclesRef.current = vehicles
  const focusFallbackRef = useRef(focusFallback)
  focusFallbackRef.current = focusFallback

  useEffect(() => {
    const map = mapRef.current
    if (!map || !ready.current || !selected || replay?.playhead) return
    const v = vehiclesRef.current.find((x) => x.vehicle_id === selected)
    const center = resolveBusFocus(v, focusFallbackRef.current)
    if (!center) return
    map.easeTo({
      center,
      zoom: Math.max(map.getZoom(), BUS_FOCUS_MIN_ZOOM),
      duration: 450,
    })
  }, [selected, focusTick, replay, mapReady])

  // Snap the Up/Down toggle to the newly-selected vehicle's sticky run direction -- without
  // this, `activeStopDirection` stayed wherever the toggle last landed (the 'up' default,
  // on first load), so picking a bus actually running Down kept highlighting Up's stops
  // until an operator noticed and clicked the toggle by hand. Skipped when the parent
  // controls direction (BusOpsShell passes geotag-inferred `stopDirection` +
  // `onStopDirectionChange`) -- sticky GPS nearest-stop flips on shared Up/Down stops and
  // must not overwrite the geotag signal. Keyed on `selected` alone (not `vehicles`) so a
  // manual toggle after this sync isn't fought by the next live poll.
  useEffect(() => {
    if (onStopDirectionChange) return
    if (!selected) return
    const dir = vehiclesRef.current.find((v) => v.vehicle_id === selected)?.route_direction
    if (dir === 'up' || dir === 'down') setStopDirection(dir)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected])

  return (
    <>
      <div ref={container} className="h-full w-full" />
      {highlightRoute && (
        <div className="pointer-events-auto absolute left-3 top-14 z-10 flex overflow-hidden rounded-full bg-white/90 shadow-[0_1px_2px_rgba(15,23,42,0.12)] backdrop-blur">
          {(['up', 'down'] as const).map((d) => (
            <button
              key={d}
              type="button"
              onClick={() => setStopDirection(d)}
              className={clsx(
                'px-3 py-1.5 text-[11px] font-semibold capitalize transition-colors',
                activeStopDirection === d
                  ? 'bg-[#0F172A] text-white'
                  : 'text-[#0F172A] hover:bg-[#EEF2F7]',
              )}
            >
              {d}
            </button>
          ))}
        </div>
      )}
      {/* bottom-12, not bottom-3: MapLibre's own ScaleControl (added imperatively above,
          not React JSX -- easy to miss when surveying "what else is in this corner")
          already occupies bottom-left at `bottom:0` with a 10px margin, maplibre-gl's
          own CSS. bottom-3 painted the legend directly over it. */}
      {showFleetLegend && (
        <div className="pointer-events-auto absolute bottom-12 left-3 z-10">
          <MapLegendCard title="Fleet" rows={legendRows} footer="Counts are live from telemetry" />
        </div>
      )}
      <div className="pointer-events-auto absolute bottom-3 right-3 z-10 flex flex-col items-end gap-1">
        <button
          type="button"
          onClick={() => { setView3D((v) => !v); setPhotorealOn(false) }}
          title="Real 3D buildings, plus camera tilt and orbit on a route or bus selection -- free, no API key, works offline"
          className={clsx(
            'rounded-full px-3 py-1.5 text-[11px] font-semibold shadow-[0_1px_2px_rgba(15,23,42,0.12)] backdrop-blur transition-colors',
            view3D
              ? 'bg-[#0F172A] text-white'
              : 'bg-white/90 text-[#0F172A] hover:bg-white',
          )}
        >
          {view3D ? '3D view' : '2D view'}
        </button>
        <button
          type="button"
          disabled={!photorealAvailable}
          onClick={() => { setPhotorealOn((v) => !v); setView3D(false) }}
          title={
            photorealAvailable
              ? 'Google Photorealistic 3D Tiles. Billed per session, needs internet.'
              : 'Set GOOGLE_MAPS_API_KEY on the serving service to enable.'
          }
          className={clsx(
            'rounded-full px-3 py-1.5 text-[11px] font-semibold shadow-[0_1px_2px_rgba(15,23,42,0.12)] backdrop-blur transition-colors disabled:cursor-not-allowed disabled:opacity-40',
            photorealOn
              ? 'bg-[#D97706] text-white'
              : 'bg-white/90 text-[#0F172A] hover:bg-white',
          )}
        >
          photoreal 3D
        </button>
        {photorealOn && photorealAttribution.length > 0 && (
          <div className="max-w-[220px] truncate rounded-full bg-white/80 px-2 py-0.5 text-[10px] text-[#64748B] backdrop-blur"
               title={photorealAttribution.join(' · ')}>
            {photorealAttribution.join(' · ')}
          </div>
        )}
      </div>
    </>
  )
}
